/**
 * Screen wake lock for hands-free voice practice.
 *
 * A spoken practice turn is the one time the user is deliberately NOT touching
 * the phone — they are talking to it, or listening to it. The OS idle timer
 * can't tell that apart from an abandoned app, so it dims and locks mid-sentence,
 * which kills the mic session and cuts the coach off until the screen is woken.
 * While voice mode is on we hold the screen awake so the conversation can run to
 * its natural end.
 *
 * Held under our OWN tag rather than the default one: expo-keep-awake counts
 * tags independently, so this can never release a lock some other part of the
 * app is holding (or have its own released by one).
 *
 * Every call is best-effort. A wake lock is a nicety — if the platform refuses
 * (web without the Screen Wake Lock API, a permission policy that blocks it, a
 * release for a tag the native side no longer knows about) the practice should
 * carry on exactly as it does today, not fail.
 */
import { activateKeepAwakeAsync, deactivateKeepAwake } from "expo-keep-awake";

const TAG = "opncue-voice-practice";

/**
 * Ask the OS to keep the screen on. Safe to call when already held — re-arming
 * is how the web path recovers, since browsers drop a wake lock whenever the tab
 * is hidden and never restore it themselves.
 */
export function holdScreenAwake(): void {
  void activateKeepAwakeAsync(TAG).catch(() => {});
}

/** Let the screen sleep normally again. Safe to call when nothing is held. */
export function releaseScreenAwake(): void {
  void deactivateKeepAwake(TAG).catch(() => {});
}
